import React, { useMemo } from "react";
import { Link } from "react-router-dom";

import { Box, Typography, alpha, useTheme, styled, darken } from "@mui/material";
import { Article, ChevronRight, AccessTime } from "@mui/icons-material";
import CustomIcon from "src/components/custom-icon";

// Constantes de design
const TRANSITION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)';
const TRANSITION_DURATION = '0.25s';
const BORDER_COLOR = '#f0f0f0';

// Estilos base reutilizáveis
const truncateText = (lines = 2) => ({
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical'
});

const baseTransition = `all ${TRANSITION_DURATION} ${TRANSITION_EASING}`;

// Componente estilizado para o container principal (Link)
const CardContainer = styled(Link, {
    shouldForwardProp: (prop) => !['accentColor', 'darkenAccentColor', 'isLast'].includes(prop)
})(({ theme, accentColor, darkenAccentColor, isLast }) => ({
    display: 'flex',
    alignItems: 'flex-start',
    padding: theme.spacing(2.5, 2),
    [theme.breakpoints.up('sm')]: {
        padding: theme.spacing(3, 3)
    },
    cursor: 'pointer',
    textDecoration: 'none',
    color: 'inherit',
    margin: 8,
    transition: baseTransition,
    position: 'relative',
    overflow: 'hidden',
    borderBottom: isLast ? 'none' : `1px solid ${BORDER_COLOR}`,

    // Indicador lateral
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: '50%',
        transform: 'translateY(-50%)',
        width: 4,
        height: '0%',
        backgroundColor: darkenAccentColor,
        borderRadius: '0 4px 4px 0',
        transition: `height ${TRANSITION_DURATION} ${TRANSITION_EASING}`
    },

    // Efeitos de hover, foco e ativo
    '&:hover': {
        textDecoration: 'none',
        backgroundColor: alpha(darken(accentColor, 0.2), 0.1),
        '&::before': { height: '70%' },
        '& .article-icon-container': {
            transform: 'scale(1.05)',
            '& .article-icon': { transform: 'scale(1.05)' }
        },
        '& .article-title': { color: darken(accentColor, 0.3) },
        '& .article-arrow': { opacity: 1, transform: 'translateX(4px)' }
    },

    '&:focus-visible': {
        outline: 'none',
        textDecoration: 'none',
        backgroundColor: alpha(accentColor, 0.06),
        '&::before': { height: '90%' }
    },

    '&:active': {
        outline: 'none',
        textDecoration: 'none',
        backgroundColor: alpha(accentColor, 0.08),
        '&::before': { height: '100%' }
    },

    '&:visited': {
        color: 'inherit'
    }
}));

// Componente estilizado para o container do ícone
const IconContainer = styled(Box)(({ theme }) => ({
    width: 44,
    height: 44,
    [theme.breakpoints.up('sm')]: {
        width: 52,
        height: 52
    },
    borderRadius: 12,
    backgroundColor: alpha(theme.palette.text.secondary, 0.1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
        marginRight: theme.spacing(2.5)
    },
    flexShrink: 0,
    transition: baseTransition
}));

// Componente estilizado para o título
const Title = styled(Typography)(({ theme }) => ({
    fontWeight: 600,
    fontSize: '1rem',
    [theme.breakpoints.up('sm')]: {
        fontSize: '1.1rem'
    },
    color: '#000',
    lineHeight: 1.4,
    marginBottom: theme.spacing(0.5),
    transition: `color ${TRANSITION_DURATION} ${TRANSITION_EASING}`,
    ...truncateText(2)
}));

// Componente estilizado para o subtítulo
const Subtitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    [theme.breakpoints.up('sm')]: {
        fontSize: '0.9rem'
    },
    lineHeight: 1.5,
    marginBottom: theme.spacing(1),
    ...truncateText(2)
}));

const ArticleCard = ({ article, clientportal = {}, isLast = false }) => {
    const theme = useTheme();

    // Cor de destaque (memoizada para evitar recálculos)
    const accentColor = useMemo(
        () => clientportal.defaultcolor || theme.palette.primary.main,
        [clientportal.defaultcolor, theme.palette.primary.main]
    );

    const darkenAccentColor = useMemo(
        () => darken(accentColor, 0.09),
        [accentColor]
    );

    // Obtém o idioma da URL
    const getLanguage = () => {
        const language = window.location.pathname.split('/')[1];
        return ['pt', 'es', 'en'].includes(language) ? language : 'pt';
    };

    return (
        <CardContainer
            key={article.id}
            to={`/${getLanguage()}/article-external/${article.id}`}
            accentColor={accentColor}
            darkenAccentColor={darkenAccentColor}
            isLast={isLast}
        >
            {/* Ícone do artigo */}
            <IconContainer className="article-icon-container">
                <CustomIcon
                    icon={Article}
                    color={darkenAccentColor}
                    iconSx={{
                        fontSize: { xs: 22, sm: 26 },
                        transition: `color ${TRANSITION_DURATION} ${TRANSITION_EASING}`
                    }}
                />
            </IconContainer>

            {/* Conteúdo do artigo */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Title className="article-title" variant="h6">
                    {article.title}
                </Title>

                {article.subtitle && (
                    <Subtitle variant="body2" sx={{ color: 'text.secondary' }}>
                        {article.subtitle}
                    </Subtitle>
                )}

                {article.readingtime && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: article.subtitle ? 0 : 0.5 }}>
                        <CustomIcon
                            icon={AccessTime}
                            color="text.secondary"
                            iconSx={{ fontSize: 14 }}
                        />

                        <Typography
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                fontSize: '0.75rem',
                                fontWeight: 500,
                                lineHeight: 'normal'
                            }}
                        >
                            {article.readingtime}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Seta indicadora */}
            <CustomIcon
                icon={ChevronRight}
                className="article-arrow"
                color={darkenAccentColor}
                iconSx={{
                    fontSize: { xs: 24, sm: 28 },
                    opacity: 0.3,
                    ml: 1,
                    flexShrink: 0,
                    transition: baseTransition
                }}
            />
        </CardContainer>
    );
};

export default ArticleCard;